import type {
  Post,
  PostExcerpt,
  Category,
  CategoryPage,
  Pagination,
  Blog,
  Settings,
  AuthorPost,
  Translation,
  TableOfContents,
} from 'types';
import type { QueryProps } from '../api';
import type {
  GetServerSidePropsContext,
  GetServerSidePropsResult,
  GetStaticPropsContext,
  GetStaticPropsResult,
} from 'next';
import camelcaseKeys from 'camelcase-keys';
import { request } from 'lib/rest';
import { getSettings } from './settings';
import { getPageData } from 'lib/queries/pages';
import { extractH2Data } from 'lib/utils/extractH2Data';
import { countWordsFromHtmlServer } from 'lib/utils/calculateHtmlReadTime';

export function getBlogUrl({
  defaultLocale,
  locale,
}: Pick<QueryProps, 'defaultLocale' | 'locale'>): string {
  const localePath = locale === defaultLocale ? '' : `/${locale}`;

  return `${localePath}/blog`;
}

export type PostsList = {
  locale: string;
  page: number;
  perPage: number;
  isLatestNews?: boolean;
  isStickyPosts?: boolean;
  categorySlug?: never;
  authorSlug?: never;
  postsList?: 'writer' | 'reviewer' | 'expert' | null;
};

export type PostsListLatestNews = PostsList & { isLatestNews: boolean };

export type PostsListSticky = PostsList & { isStickyPosts: boolean };

export type PostListCategory = PostsList & { categorySlug: string };

export type PostListAuthor = PostsList & { authorSlug: string };

export type PostListParams =
  | PostsList
  | PostsListLatestNews
  | PostsListSticky
  | PostListCategory;

export type PostsListResult<T> = T extends
  | PostsList
  | PostListCategory
  | PostListAuthor
  ? {
      posts: PostExcerpt[];
      pagination: Pagination;
    }
  : { posts: PostExcerpt[] };

// this value could come from an env var
const newsCategoryId = '1171';

export async function getPostsList<T extends PostListParams>({
  locale,
  page = 1,
  perPage = 6,
  isLatestNews,
  isStickyPosts,
  categorySlug,
  authorSlug,
  postsList = null,
}: T): Promise<PostsListResult<T>> {
  const fields =
    'id,title,date,modified,link,featured_media,categories,post_author,seo,translations,language,type';
  let endpoint = `/posts?_fields=${fields}&per_page=${perPage}&page=${page}`;

  if (isLatestNews) {
    endpoint += `&categories[]=${newsCategoryId}`;
  }

  if (isStickyPosts) {
    endpoint += `&categories_exclude[]=${newsCategoryId}`;
  }

  if (categorySlug) {
    endpoint += `&category=${categorySlug}`;
  }

  if (authorSlug) {
    endpoint += `&author_slug=${authorSlug}`;
  }

  if (postsList) {
    endpoint += `&posts_list=${postsList}`;
  }

  const { error, data } = await request<PostExcerpt[]>(endpoint, {
    headers: {
      'Accept-Language': locale,
    },
  });

  if (error) {
    throw error;
  }

  const posts = camelcaseKeys(data.items, { deep: true });

  if (isLatestNews || isStickyPosts) {
    return {
      posts,
    } as PostsListResult<T>;
  }

  return {
    posts,
    pagination: {
      total: data.totalItems,
      limit: perPage,
      offset: (page - 1) * perPage,
    },
  } as PostsListResult<T>;
}

export type CategoriesResult = Category[];

export async function getCategories(locale: string): Promise<CategoriesResult> {
  const { error, data } = await request<CategoriesResult>(
    `/categories?_fields=id,name,link,seo,translations,language,taxonomy`,
    {
      headers: {
        'Accept-Language': locale,
      },
    },
  );

  if (error) {
    throw error;
  }

  return camelcaseKeys(data.items, { deep: true });
}

export type BlogPageProps = {
  page: Blog;
  postsList: PostsListResult<PostsList>;
  latestNews: PostsListResult<PostsListLatestNews>['posts'];
  stickyPosts: PostsListResult<PostsListSticky>['posts'];
  settings: Settings;
  categories: CategoriesResult;
  showFullPage: boolean;
  isPreview: boolean;
};

export async function getBlogPage({
  query,
  locale,
  preview,
}: GetServerSidePropsContext): Promise<
  GetServerSidePropsResult<BlogPageProps>
> {
  const page = query.page ? Number(query.page) : 1;

  if (page === 0) {
    return {
      notFound: true,
    };
  }

  try {
    const [blogPage, settings, postsList, latestNews, stickyPosts, categories] =
      await Promise.all([
        getPageData({ slug: '/blog', locale, preview }),
        getSettings(locale),
        getPostsList({ locale, page, perPage: 6 } as PostsList),
        getPostsList({
          locale,
          page: 1,
          perPage: 4,
          isLatestNews: true,
        } as PostsListLatestNews),
        getPostsList({
          locale,
          page: 1,
          perPage: 4,
          isStickyPosts: true,
        } as PostsListSticky),
        getCategories(locale),
      ]);

    if (!blogPage || !settings || !postsList || !categories) {
      return {
        notFound: true,
      };
    }

    //generate languageAlternates - hreflang - array
    const hrefLangAnnotations = () => {
      if (blogPage?.translations?.length > 0) {
        const annotations: Omit<Translation, 'locale'>[] =
          blogPage?.translations;

        if (
          locale === 'en' ||
          blogPage?.translations?.find(({ slug }) => slug === 'en')
        ) {
          //hreflang must have x-default
          annotations.push({
            uri:
              locale === 'en'
                ? '/blog'
                : blogPage?.translations?.find(({ slug }) => slug === 'en')
                    ?.uri,
            slug: 'x-default',
          });
        }
        return annotations;
      }
      return null;
    };

    const languageAlternates = hrefLangAnnotations()
      ? hrefLangAnnotations()?.map(({ slug, uri }) => ({
          hrefLang: slug,
          href: uri ? `https://leafwell.com${uri}` : null,
        }))
      : null;

    return {
      props: {
        page: blogPage,
        settings: {
          ...settings,
          pageTranslations: blogPage.translations,
          languageAlternates,
        },
        postsList: postsList,
        latestNews: latestNews.posts,
        stickyPosts: stickyPosts.posts,
        categories,
        showFullPage: page === 1,
        isPreview: !!preview,
      },
    };
  } catch (error) {
    console.log(`error getting blog:`, JSON.stringify(error));

    if (error.status === 400) {
      return {
        notFound: true,
      };
    }

    throw error;
  }
}

export async function getCategoryPageData({
  locale,
  slug,
  preview,
}: {
  locale: string;
  slug: string;
  preview: boolean;
}): Promise<CategoryPage> {
  const { error, data } = await request<CategoryPage>(
    `/categories?slug=${slug}&_fields=id,name,slug,link,seo,translations,language,taxonomy${
      preview ? '&status=draft' : ''
    }`,
    {
      headers: {
        'Accept-Language': locale,
      },
    },
  );

  if (error) {
    throw error;
  }

  return camelcaseKeys(data.items[0], { deep: true });
}

export type CategoryPageProps = {
  page: CategoryPage;
  settings: Settings;
  postsList: PostsListResult<PostsList>;
  categories: CategoriesResult;
  isPreview: boolean;
};

export async function getCategoryPage({
  query,
  params,
  locale,
  preview,
}: GetServerSidePropsContext): Promise<
  GetServerSidePropsResult<CategoryPageProps>
> {
  const slug = Array.isArray(params.slug)
    ? params.slug[params.slug.length - 1]
    : params.slug;
  const page = query.page ? Number(query.page) : 1;

  if (page === 0) {
    return {
      notFound: true,
    };
  }

  try {
    const [categoryPage, settings, postsList, categories] = await Promise.all([
      getCategoryPageData({ locale, slug, preview }),
      getSettings(locale),
      getPostsList({
        locale,
        page,
        perPage: 6,
        categorySlug: slug,
      } as PostsList),
      getCategories(locale),
    ]);

    if (!categoryPage || !settings || !postsList || !categories) {
      return {
        notFound: true,
      };
    }

    //generate languageAlternates - hreflang - array
    const hrefLangAnnotations = () => {
      if (categoryPage?.translations?.length > 0) {
        const annotations: Omit<Translation, 'locale'>[] =
          categoryPage?.translations;

        if (
          locale === 'en' ||
          categoryPage?.translations?.find(({ slug }) => slug === 'en')
        ) {
          //hreflang must have x-default
          annotations.push({
            uri:
              locale === 'en'
                ? `/blog/category/${slug}`
                : categoryPage?.translations?.find(({ slug }) => slug === 'en')
                    ?.uri,
            slug: 'x-default',
          });
        }
        return annotations;
      }
      return null;
    };

    const languageAlternates = hrefLangAnnotations()
      ? hrefLangAnnotations()?.map(({ slug, uri }) => ({
          hrefLang: slug,
          href: uri ? `https://leafwell.com${uri}` : null,
        }))
      : null;

    return {
      props: {
        page: categoryPage,
        settings: {
          ...settings,
          pageTranslations: categoryPage.translations,
          languageAlternates,
        },
        postsList,
        categories,
        isPreview: !!preview,
      },
    };
  } catch (error) {
    console.log(`error getting category ${slug}:`, JSON.stringify(error));

    if (error.status === 400) {
      return {
        notFound: true,
      };
    }

    throw error;
  }
}

async function getPostData({
  locale,
  slug,
  preview,
}: {
  locale: string;
  slug: string;
  preview: boolean;
}): Promise<Post> {
  const { error, data } = await request<Post>(
    `/posts?slug=${slug}&_fields=id,title,date,modified,slug,link,content,seo,acf,featured_media,categories,post_author,translations,language,type${
      preview ? '&status=draft' : ''
    }`,
    {
      headers: {
        'Accept-Language': locale,
      },
    },
  );

  if (error) {
    throw error;
  }

  return camelcaseKeys(data.items[0], { deep: true });
}

export type SinglePostProps = {
  post?: Post;
  tableOfContents?: TableOfContents[];
  readTime?: {
    words: number;
    minutes: number;
    seconds: number;
    wpm: number;
  };
  settings: Settings;
  isPreview: boolean;
};

export async function getSinglePost({
  locale,
  params,
  preview,
}: GetStaticPropsContext): Promise<GetStaticPropsResult<SinglePostProps>> {
  const slug = params.slug as string;

  try {
    const [post, settings] = await Promise.all([
      getPostData({ locale, slug, preview }),
      getSettings(locale),
    ]);

    if (!post || !settings) {
      return {
        notFound: true,
      };
    }

    const keyTakeaways = post?.acf?.keyTakeaways;
    const content = keyTakeaways ? keyTakeaways + post?.content : post?.content;
    const tableOfContents = extractH2Data(content);

    const readTime = countWordsFromHtmlServer(content, { wpm: 250 });
    //generate languageAlternates - hreflang - array
    const hrefLangAnnotations = () => {
      if (post?.translations?.length > 0) {
        const annotations: Omit<Translation, 'locale'>[] = post?.translations;

        if (
          locale === 'en' ||
          post?.translations?.find(({ slug }) => slug === 'en')
        ) {
          //hreflang must have x-default
          annotations.push({
            uri:
              locale === 'en'
                ? `/blog/${slug}`
                : post?.translations?.find(({ slug }) => slug === 'en')?.uri,
            slug: 'x-default',
          });
        }
        return annotations;
      }
      return null;
    };

    const languageAlternates = hrefLangAnnotations()
      ? hrefLangAnnotations()?.map(({ slug, uri }) => ({
          hrefLang: slug,
          href: uri ? `https://leafwell.com${uri}` : null,
        }))
      : null;

    return {
      props: {
        post,
        tableOfContents,
        readTime,
        settings: {
          ...settings,
          pageTranslations: post.translations,
          languageAlternates,
        },
        isPreview: !!preview,
      },
    };
  } catch (error) {
    console.log(`error getting post ${slug}:`, JSON.stringify(error));

    if (error.status === 400) {
      return {
        notFound: true,
      };
    }

    throw error;
  }
}

type AuthorResult = AuthorPost & {
  language: string;
  translations: Translation[];
};

async function getAuthorData({
  locale,
  slug,
  preview,
}: {
  locale: string;
  slug: string;
  preview: boolean;
}): Promise<AuthorResult> {
  const { error, data } = await request<AuthorResult>(
    `/post_author?slug=${slug}&_fields=id,name,modified,slug,seo,lang,acf,taxonomy,translations,language${
      preview ? '&status=draft' : ''
    }`,
    {
      headers: {
        'Accept-Language': locale,
      },
    },
  );

  if (error) {
    throw error;
  }

  return camelcaseKeys(data.items[0], { deep: true });
}

export type AuthorPageProps = {
  page: AuthorResult;
  settings: Settings;
  postsList: PostsListResult<PostListAuthor>;
  reviewPostsList: PostsListResult<PostListAuthor>;
  expertPostsList: PostsListResult<PostListAuthor>;
  isPreview: boolean;
};

export async function getAuthorPage({
  locale,
  query,
  params,
  preview,
}: GetServerSidePropsContext): Promise<
  GetServerSidePropsResult<AuthorPageProps>
> {
  const slug = params?.slug as string;
  const page = query.page ? Number(query.page) : 1;
  const reviewPage = query.review_page ? Number(query.review_page) : 1;
  const expertPage = query.expert_page ? Number(query.expert_page) : 1;

  try {
    const [authorPage, settings, postsList, reviewPostsList, expertPostsList] =
      await Promise.all([
        getAuthorData({ locale, slug, preview }),
        getSettings(locale),
        getPostsList({
          locale,
          page,
          perPage: 7,
          authorSlug: slug,
          postsList: 'writer',
        } as PostsList),
        getPostsList({
          locale,
          page: reviewPage,
          perPage: 7,
          authorSlug: slug,
          postsList: 'reviewer',
        } as PostsList),
        getPostsList({
          locale,
          page: expertPage,
          perPage: 7,
          authorSlug: slug,
          postsList: 'expert',
        } as PostsList),
      ]);

    if (!authorPage || !settings || !postsList) {
      return {
        notFound: true,
      };
    }

    //generate languageAlternates - hreflang - array
    const hrefLangAnnotations = () => {
      if (authorPage?.translations?.length > 0) {
        const annotations: Omit<Translation, 'locale'>[] =
          authorPage?.translations;

        if (
          locale === 'en' ||
          authorPage?.translations?.find(({ slug }) => slug === 'en')
        ) {
          //hreflang must have x-default
          annotations.push({
            uri:
              locale === 'en'
                ? `/blog/authors/${slug}`
                : authorPage?.translations?.find(({ slug }) => slug === 'en')
                    ?.uri,
            slug: 'x-default',
          });
          return annotations;
        }
      }
      return null;
    };

    const languageAlternates = hrefLangAnnotations()
      ? hrefLangAnnotations()?.map(({ slug, uri }) => ({
          hrefLang: slug,
          href: uri ? `https://leafwell.com${uri}` : null,
        }))
      : null;

    return {
      props: {
        page: authorPage,
        postsList,
        reviewPostsList,
        expertPostsList,
        settings: {
          ...settings,
          pageTranslations: authorPage.translations,
          languageAlternates,
        },
        isPreview: !!preview,
      },
    };
  } catch (error) {
    console.log(`Error getting author ${slug}:`, error);

    if (error.status === 400) {
      return {
        notFound: true,
      };
    }

    throw error;
  }
}
